import { useState, useEffect } from "react";
import background from "./images/background.png";
import GameModal, { MODAL_TYPES } from "./GameModal";
import duck from "./images/duck-easy.png";
import star from "./images/star.png";
import { useGameContext } from "./GameContext";
import { useSaveScore } from "../utils/useSaveScore";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_API_URL || "";

const GRID_SIZE = 12;
const DIRECTIONS = [
  [0, 1],
  [1, 0],
  [1, 1],
  [0, -1],
  [-1, 0],
  [-1, -1],
  [1, -1],
  [-1, 1],
];
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const COLORS = [
  "rgba(176,106,179,0.6)",
  "rgba(99,179,237,0.6)",
  "rgba(252,129,74,0.6)",
  "rgba(104,211,145,0.6)",
  "rgba(246,173,85,0.6)",
  "rgba(237,100,166,0.6)",
  "rgba(129,140,248,0.6)",
  "rgba(52,211,153,0.6)",
];

const normalize = (str) =>
  str
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z]/g, "");
const key = (r, c) => `${r}-${c}`;

function buildGrid(words) {
  const grid = Array.from({ length: GRID_SIZE }, () =>
    Array(GRID_SIZE).fill("")
  );
  const placed = [];
  let hasCross = false;

  const tryPlace = (word, forceCross = false) => {
    const norm = normalize(word);
    const shuffledDirs = [...DIRECTIONS].sort(() => Math.random() - 0.5);
    const crossingCandidates = [];
    const freeCandidates = [];
    for (const dir of shuffledDirs) {
      for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
          const cells = [];
          let fits = true;
          let crosses = 0;
          for (let i = 0; i < norm.length; i++) {
            const r = row + dir[0] * i;
            const c = col + dir[1] * i;
            if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) {
              fits = false;
              break;
            }
            if (grid[r][c] !== "" && grid[r][c] !== norm[i]) {
              fits = false;
              break;
            }
            if (grid[r][c] === norm[i]) crosses++;
            cells.push({ r, c, letter: norm[i] });
          }
          if (fits) {
            if (crosses > 0) crossingCandidates.push({ cells });
            else freeCandidates.push({ cells });
          }
        }
      }
    }
    let candidates;
    if (forceCross && crossingCandidates.length > 0) {
      candidates = crossingCandidates.sort(() => Math.random() - 0.5);
      hasCross = true;
    } else {
      candidates = [...freeCandidates, ...crossingCandidates].sort(
        () => Math.random() - 0.5
      );
    }
    if (candidates.length === 0) return false;
    const chosen = candidates[0];
    chosen.cells.forEach(({ r, c, letter }) => (grid[r][c] = letter));
    placed.push({ word, norm, cells: chosen.cells });
    return true;
  };

  words.forEach((word, i) => {
    const forceCross = !hasCross && i >= 1 && placed.length >= 1;
    tryPlace(word, forceCross);
  });
  for (let r = 0; r < GRID_SIZE; r++)
    for (let c = 0; c < GRID_SIZE; c++)
      if (grid[r][c] === "")
        grid[r][c] = LETTERS[Math.floor(Math.random() * LETTERS.length)];
  return { grid, placed };
}

function getLineCells(start, end) {
  if (!start || !end) return [start].filter(Boolean);
  const dr = end.r - start.r;
  const dc = end.c - start.c;
  const steps = Math.max(Math.abs(dr), Math.abs(dc));
  if (steps === 0) return [start];
  if (Math.abs(dr) !== 0 && Math.abs(dc) !== 0 && Math.abs(dr) !== Math.abs(dc))
    return [start];
  const stepR = dr === 0 ? 0 : dr / Math.abs(dr);
  const stepC = dc === 0 ? 0 : dc / Math.abs(dc);
  return Array.from({ length: steps + 1 }, (_, i) => ({
    r: start.r + stepR * i,
    c: start.c + stepC * i,
  }));
}

const isTouchDevice = () => window.matchMedia("(hover: none)").matches;
const RECENT_QUOTES_KEY = "kk_recent_quotes";
const MAX_RECENT_QUOTES = 56;
const getRecentQuotes = () => {
  try {
    return JSON.parse(sessionStorage.getItem(RECENT_QUOTES_KEY) || "[]");
  } catch {
    return [];
  }
};
const saveRecentQuotes = (arr) => {
  try {
    sessionStorage.setItem(RECENT_QUOTES_KEY, JSON.stringify(arr));
  } catch {}
};

export default function SopaDeLetras() {
  const [grid, setGrid] = useState([]);
  const [placedWords, setPlacedWords] = useState([]);
  const [wordList, setWordList] = useState([]);
  const [foundWords, setFoundWords] = useState(new Set());
  const [highlightedCells, setHighlightedCells] = useState(new Map());
  const [firstTap, setFirstTap] = useState(null);
  const [previewCells, setPreviewCells] = useState([]);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [won, setWon] = useState(false);
  const [activeWord, setActiveWord] = useState(null);
  const [successFlash, setSuccessFlash] = useState(false);
  const [isTouch] = useState(() => isTouchDevice());
  const { saveScore, saveRecord, fetchTodayScores } = useSaveScore();
  const { token } = useAuth();
  const { setIsPlaying, setScore } = useGameContext();

  const [sopasHoy, setSopasHoy] = useState(0);
  const [sopasEver, setSopasEver] = useState(0);

  // Cargar récord y hoy desde DB
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.record_sopas) setSopasEver(data.record_sopas);
      })
      .catch(() => {});

    fetchTodayScores().then((data) => {
      if (data?.sopas_hoy) setSopasHoy(Number(data.sopas_hoy));
    });
  }, [token]);

  const incrementSopas = () => {
    const nextHoy = sopasHoy + 1;
    setSopasHoy(nextHoy);
    if (nextHoy > sopasEver) {
      setSopasEver(nextHoy);
      saveRecord("sopas", nextHoy);
    }
  };

  useEffect(() => {
    setIsPlaying(true);
    return () => {
      setIsPlaying(false);
      setScore(0);
    };
  }, []);

  const placedWordList = wordList.filter(({ word }) =>
    placedWords.some((pw) => pw.norm === normalize(word))
  );

  const loadGame = async () => {
    setLoading(true);
    setError(null);
    setFirstTap(null);
    setPreviewCells([]);
    setScore(0);
    try {
      const res = await fetch("/api/quotes/all");
      if (!res.ok) throw new Error("No se pudieron cargar las frases");
      const quotes = await res.json();
      const recentQuotes = getRecentQuotes();
      const freshQuotes = quotes.filter((q) => !recentQuotes.includes(q.text));
      const pool = freshQuotes.length >= 8 ? freshQuotes : quotes;
      const candidates = pool
        .map((q) => {
          const words = q.text
            .split(" ")
            .map((w) => ({
              word: w.replace(/[^a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/gi, "").toUpperCase(),
              quote: q.text,
            }))
            .filter(({ word }) => word.length >= 5);
          if (!words.length) return null;
          return words[Math.floor(Math.random() * words.length)];
        })
        .filter(Boolean)
        .filter(
          ({ word }, i, arr) => arr.findIndex((x) => x.word === word) === i
        );
      const shuffled = candidates.sort(() => Math.random() - 0.5).slice(0, 8);
      const updatedRecent = getRecentQuotes();
      shuffled.forEach(({ quote }) => {
        if (!updatedRecent.includes(quote)) {
          updatedRecent.push(quote);
          if (updatedRecent.length > MAX_RECENT_QUOTES) updatedRecent.shift();
        }
      });
      saveRecentQuotes(updatedRecent);
      const { grid: g, placed } = buildGrid(shuffled.map((x) => x.word));
      setGrid(g);
      setPlacedWords(placed);
      setWordList(shuffled);
      setFoundWords(new Set());
      setHighlightedCells(new Map());
      setWon(false);
      const firstPlaced = shuffled.find(({ word }) =>
        placed.some((pw) => pw.norm === normalize(word))
      );
      setActiveWord(firstPlaced?.word || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGame();
  }, []);

  const currentWord =
    wordList.find((w) => w.word === activeWord) || placedWordList[0];
  const highlightedQuote = currentWord
    ? (() => {
        const quote = currentWord.quote;
        const wordRaw =
          currentWord.word.charAt(0) + currentWord.word.slice(1).toLowerCase();
        return quote.replace(new RegExp(`(${wordRaw})`, "gi"), "《$1》");
      })()
    : "";

  const handleCellTap = (r, c) => {
    if (!firstTap) {
      setFirstTap({ r, c });
      setPreviewCells([{ r, c }]);
    } else {
      const cells = getLineCells(firstTap, { r, c });
      const selectedStr = cells.map(({ r, c }) => grid[r][c]).join("");
      const reversed = selectedStr.split("").reverse().join("");
      const match = placedWords.find(
        (pw) =>
          !foundWords.has(pw.word) &&
          (pw.norm === selectedStr || pw.norm === reversed)
      );
      if (match) {
        const color =
          COLORS[
            placedWordList.findIndex((w) => normalize(w.word) === match.norm) %
              COLORS.length
          ];
        setHighlightedCells((prev) => {
          const next = new Map(prev);
          match.cells.forEach(({ r, c }) => next.set(key(r, c), color));
          return next;
        });
        const newFound = new Set(foundWords);
        newFound.add(match.word);
        setFoundWords(newFound);
        setScore(newFound.size);
        setSuccessFlash(true);
        setTimeout(() => setSuccessFlash(false), 600);
        const nextWord = placedWordList.find(
          (w) => w.word !== match.word && !newFound.has(w.word)
        );
        setActiveWord(nextWord?.word || match.word);
        if (newFound.size === placedWords.length) {
          incrementSopas();
          saveScore("sopa_letras", 1);
          setTimeout(() => setWon(true), 500);
        }
      } else {
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
      setFirstTap(null);
      setPreviewCells([]);
    }
  };

  const handleCellHover = (r, c) => {
    if (isTouch || !firstTap) return;
    setPreviewCells(getLineCells(firstTap, { r, c }));
  };
  const cancelSelection = () => {
    setFirstTap(null);
    setPreviewCells([]);
  };
  const previewSet = new Set(previewCells.map(({ r, c }) => key(r, c)));

  if (loading)
    return (
      <div
        className="flex items-center justify-center min-h-screen font-quicksand"
        style={{
          backgroundImage: `url(${background})`,
          backgroundRepeat: "repeat-y",
          backgroundSize: "100% auto",
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="animate-spin rounded-full h-8 w-8 border-2"
            style={{ borderColor: "#b06ab3", borderTopColor: "transparent" }}
          />
          <p style={{ color: "#9b7eaa" }} className="text-sm">
            Preparando el juego...
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div
        className="flex items-center justify-center min-h-screen font-quicksand p-5"
        style={{
          backgroundImage: `url(${background})`,
          backgroundRepeat: "repeat-y",
          backgroundSize: "100% auto",
        }}
      >
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadGame}
            className="px-6 py-2 rounded-2xl text-white font-bold"
            style={{ background: "#b06ab3" }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );

  return (
    <div
      className="flex flex-col min-h-screen font-quicksand pb-8"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
      }}
    >
      <div className="text-center pt-5 pb-2 px-5">
        <p
          className="text-xs font-bold tracking-widest uppercase mb-1"
          style={{ color: "#b06ab3" }}
        >
          Sopa de Letras
        </p>
        <h1 className="text-xl font-extrabold" style={{ color: "#2d1f3d" }}>
          Encuentra todas las palabras, bb
        </h1>
      </div>

      <div className="flex justify-center mt-1 mb-1">
        <div
          className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold"
          style={{
            background: "rgba(255,255,255,0.85)",
            border: "1.5px solid #f9a8d4",
            color: "#b06ab3",
            backdropFilter: "blur(8px)",
            boxShadow: "0 2px 8px rgba(176,106,179,0.12)",
          }}
        >
          <img src={duck} alt="Duck" className="w-5 h-5" />
          <span>Hoy: {sopasHoy}</span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <img src={star} alt="Star" className="w-5 h-5" />
          <span>Récord: {sopasEver}</span>
        </div>
      </div>

      {currentWord && (
        <div className="mx-4 mb-3" style={{ marginTop: "15px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "-28px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <img
              src="https://res.cloudinary.com/dbvqeiqxb/image/upload/v1772431300/karime-drawing-lmd_xtcxct.png"
              alt="Karime"
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(176,106,179,0.2)",
              }}
            />
          </div>
          <div
            className="pt-10 px-4 pb-4 rounded-2xl"
            style={{
              background: successFlash
                ? "rgba(104,211,145,0.2)"
                : "rgba(255,255,255,0.82)",
              border: `1.5px solid ${
                successFlash ? "rgba(104,211,145,0.5)" : "rgba(176,106,179,0.2)"
              }`,
              backdropFilter: "blur(8px)",
              transition: "all 0.3s",
            }}
          >
            <p
              className="text-xs font-bold uppercase tracking-wider mb-1 text-center"
              style={{ color: "#b06ab3" }}
            >
              Busca esta palabra →
            </p>
            <p
              className="text-lg font-extrabold tracking-widest mb-2 text-center"
              style={{ color: "#2d1f3d" }}
            >
              {currentWord.word.charAt(0) +
                currentWord.word.slice(1).toLowerCase()}
            </p>
            <p
              className="text-xs leading-relaxed"
              style={{ color: "#7a6080", fontStyle: "italic" }}
            >
              {highlightedQuote.split("《").map((part, i) => {
                if (i === 0) return <span key={i}>{part}</span>;
                const [bold, rest] = part.split("》");
                return (
                  <span key={i}>
                    <strong style={{ color: "#b06ab3", fontStyle: "normal" }}>
                      "{bold}"
                    </strong>
                    {rest}
                  </span>
                );
              })}
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          height: "48px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p className="text-xs px-4 text-center" style={{ color: "#c0b0cc" }}>
          {firstTap
            ? "Toca la letra final para completar la selección"
            : "Toca la primera letra de la palabra"}
        </p>
        {firstTap && (
          <button
            onClick={cancelSelection}
            className="text-xs underline mt-0.5"
            style={{ color: "#b06ab3", background: "none", border: "none" }}
          >
            Cancelar selección
          </button>
        )}
      </div>

      <div className="flex justify-center px-3">
        <div
          className={shake ? "animate-shake" : ""}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            gap: "3px",
            background: "rgba(255,255,255,0.7)",
            borderRadius: "20px",
            padding: "10px",
            boxShadow: "0 4px 20px rgba(176,106,179,0.12)",
            backdropFilter: "blur(8px)",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          {grid.map((row, r) =>
            row.map((letter, c) => {
              const k = key(r, c);
              const isAnchor = firstTap && firstTap.r === r && firstTap.c === c;
              const isPreview = previewSet.has(k) && !isAnchor;
              const highlightColor = highlightedCells.get(k);
              const isFound = !!highlightColor;
              return (
                <div
                  key={k}
                  onClick={() => handleCellTap(r, c)}
                  onMouseEnter={() => handleCellHover(r, c)}
                  style={{
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "700",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "background 0.1s, transform 0.1s",
                    background: isAnchor
                      ? "#b06ab3"
                      : isPreview
                      ? "rgba(176,106,179,0.3)"
                      : isFound
                      ? highlightColor
                      : "transparent",
                    color: isAnchor
                      ? "white"
                      : isFound || isPreview
                      ? "#2d1f3d"
                      : "#6b5878",
                    transform:
                      isAnchor || isPreview ? "scale(1.1)" : "scale(1)",
                    WebkitTapHighlightColor: "transparent",
                    boxShadow: isAnchor ? "0 0 0 2px #b06ab3" : "none",
                  }}
                >
                  {letter}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="px-5 mt-4">
        <p
          className="text-xs font-bold tracking-widest uppercase mb-2"
          style={{ color: "#b06ab3" }}
        >
          Palabras
        </p>
        <div className="flex flex-wrap gap-2">
          {placedWordList.map(({ word }, i) => {
            const isFoundWord = [...foundWords].some(
              (fw) => normalize(fw) === normalize(word)
            );
            const isCurrent = word === activeWord && !isFoundWord;
            return (
              <button
                key={word}
                onClick={() => !isFoundWord && setActiveWord(word)}
                className="px-3 py-1 rounded-full text-sm font-semibold transition-all"
                style={{
                  background: isFoundWord
                    ? COLORS[i % COLORS.length]
                    : isCurrent
                    ? "rgba(176,106,179,0.15)"
                    : "rgba(255,255,255,0.8)",
                  color: isFoundWord
                    ? "#2d1f3d"
                    : isCurrent
                    ? "#b06ab3"
                    : "#9b7eaa",
                  textDecoration: isFoundWord ? "line-through" : "none",
                  border: isCurrent
                    ? "1.5px solid #b06ab3"
                    : "1.5px solid rgba(176,106,179,0.2)",
                  cursor: isFoundWord ? "default" : "pointer",
                }}
              >
                {word.charAt(0) + word.slice(1).toLowerCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 mt-5">
        <button
          onClick={loadGame}
          className="w-full py-3 rounded-2xl text-sm font-semibold"
          style={{
            background: "rgba(255,255,255,0.8)",
            color: "#b06ab3",
            border: "1.5px solid rgba(176,106,179,0.3)",
            backdropFilter: "blur(8px)",
          }}
        >
          Échame otra ✨
        </button>
      </div>

      {won && (
        <GameModal
          isOpen={won}
          type={MODAL_TYPES.CORRECT}
          titleOverride="¡Ajá! Tu si sabes, bb"
          messageOverride="Encontraste todas las palabras."
          showConfetti={true}
          onClose={() => setWon(false)}
          onNextQuestion={() => {
            setWon(false);
            loadGame();
          }}
          onRetry={() => {
            setWon(false);
            loadGame();
          }}
        />
      )}

      <style>{`
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
